import { useEffect, useRef, useState } from "react";
import type { CSSProperties, FC } from "react";
import { chrome } from "./chrome";
import { ABOUT } from "./content";
import { pad } from "./hooks";

/** How much of each half turn the genie spends standing still, face-on, at either end. */
const REST = 0.12;

/**
 * The opening, pinned while it is read: one chrome genie to a wish. Scrolling
 * turns the genie, and every half turn brings the next genie and the next wish.
 */
export const Wishes: FC = () => {
  const section = useRef<HTMLElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const angle = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState(0);
  const count = ABOUT.wishes.length;

  useEffect(() => {
    const el = section.current;
    const stage = canvas.current;
    if (!el || !stage) return;
    const last = count - 1;
    const turn = () => {
      const room = el.offsetHeight - window.innerHeight;
      const done = room > 0 ? Math.min(1, Math.max(0, -el.getBoundingClientRect().top / room)) : 0;
      const k = Math.min(last - 1, Math.floor(done * last));
      const part = Math.min(1, Math.max(0, (done * last - k - REST) / (1 - 2 * REST)));
      return k + part * part * (3 - 2 * part);
    };
    return chrome(stage, {
      marks: ABOUT.genies,
      turn,
      onFace: setAt,
      onAngle: degrees => {
        if (angle.current) angle.current.textContent = `${String(degrees).padStart(3, "0")}°`;
      },
    });
  }, [count]);

  return (
    <section ref={section} className="sd-wishes" style={{ "--n": count } as CSSProperties}>
      <div className="sd-wishes-pin">
        <canvas ref={canvas} className="sd-wishes-genie" aria-hidden />
        <p className="sd-label">{ABOUT.label}</p>
        <p className="sd-label sd-wishes-count">
          <span>
            {pad(at + 1)} / {pad(count)}
          </span>
          <span ref={angle}>000°</span>
        </p>
        <div className="sd-wishes-text">
          <p className="sd-label">{ABOUT.intro}</p>
          <ol>
            {ABOUT.wishes.map((wish, n) => (
              <li key={wish.word} data-live={n === at || undefined}>
                <p className="sd-label">
                  wish {pad(n + 1)} / {wish.word}
                </p>
                <h2>{wish.text}</h2>
              </li>
            ))}
          </ol>
        </div>
        <p className="sd-label sd-wishes-hint">{ABOUT.hint} ↓</p>
        <h1 className="sd-wishes-title">{ABOUT.title}</h1>
      </div>
    </section>
  );
};
